import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import {
  DEFAULT_OG_IMAGE,
  getShareMetaForPath,
  getShareMetaUrl,
} from '../../config/publicShareMeta'
import { getStructuredDataForPath } from '../../config/publicStructuredData'

function upsertHeadElement<T extends HTMLElement>(
  selector: string,
  tagName: string,
  applyAttributes: (element: T) => void,
) {
  const existing = document.head.querySelector<T>(selector)
  const element = existing ?? document.createElement(tagName) as T
  applyAttributes(element)
  if (!existing) document.head.appendChild(element)
}

/** Sets page-specific Open Graph / Twitter meta and JSON-LD for public routes. */
export function PublicShareHelmet() {
  const { pathname } = useLocation()
  const meta = getShareMetaForPath(pathname)
  const url = getShareMetaUrl(meta)
  const shareTitle = meta.ogTitle ?? meta.title
  const ogImage = meta.ogImage ?? DEFAULT_OG_IMAGE
  const ogType = meta.ogType ?? 'website'
  const structuredData = getStructuredDataForPath(pathname)
  const structuredDataJson = structuredData.map((schema) => JSON.stringify(schema)).join('\n')

  useEffect(() => {
    document.title = meta.title

    upsertHeadElement<HTMLMetaElement>('meta[name="description"]', 'meta', (element) => {
      element.name = 'description'
      element.content = meta.description
    })
    upsertHeadElement<HTMLLinkElement>('link[rel="canonical"]', 'link', (element) => {
      element.rel = 'canonical'
      element.href = url
    })

    const metaTags: Array<{ selector: string; property?: string; name?: string; content: string }> = [
      { selector: 'meta[property="og:site_name"]', property: 'og:site_name', content: 'Livewello' },
      { selector: 'meta[property="og:title"]', property: 'og:title', content: shareTitle },
      { selector: 'meta[property="og:description"]', property: 'og:description', content: meta.description },
      { selector: 'meta[property="og:type"]', property: 'og:type', content: ogType },
      { selector: 'meta[property="og:url"]', property: 'og:url', content: url },
      { selector: 'meta[property="og:image"]', property: 'og:image', content: ogImage },
      { selector: 'meta[name="twitter:card"]', name: 'twitter:card', content: 'summary_large_image' },
      { selector: 'meta[name="twitter:title"]', name: 'twitter:title', content: shareTitle },
      { selector: 'meta[name="twitter:description"]', name: 'twitter:description', content: meta.description },
      { selector: 'meta[name="twitter:image"]', name: 'twitter:image', content: ogImage },
    ]

    for (const tag of metaTags) {
      upsertHeadElement<HTMLMetaElement>(tag.selector, 'meta', (element) => {
        if (tag.property) element.setAttribute('property', tag.property)
        if (tag.name) element.name = tag.name
        element.content = tag.content
      })
    }

    document
      .querySelectorAll('script[data-public-share-meta="jsonld"]')
      .forEach((element) => element.remove())
    for (const json of structuredDataJson.split('\n').filter(Boolean)) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.publicShareMeta = 'jsonld'
      script.text = json
      document.head.appendChild(script)
    }
  }, [meta.description, meta.title, ogImage, ogType, shareTitle, structuredDataJson, url])

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={url} />
      <meta property="og:site_name" content="Livewello" />
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  )
}
